import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface TicketDetail {
  ticketId: string;
  userId: string;
  subject: string;
  description: string;
  status: string;
  categoryId: string | null;
  assignedTo: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TicketMessage {
  messageId: string;
  senderId: string | null;
  senderType: string;
  content: string;
  createdAt: string;
}

interface UseTicketReturn {
  ticket: TicketDetail | null;
  messages: TicketMessage[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  addMessage: (content: string) => Promise<void>;
}

export function useTicket(ticketId: string | undefined): UseTicketReturn {
  const queryClient = useQueryClient();

  const ticketQuery = useQuery({
    queryKey: queryKeys.customerSupport.ticketDetail(ticketId ?? ''),
    queryFn: () => api.getSupportTicket(ticketId!),
    enabled: !!ticketId,
  });

  const messagesQuery = useQuery({
    queryKey: queryKeys.customerSupport.ticketMessages(ticketId ?? ''),
    queryFn: () => api.listSupportTicketMessages(ticketId!),
    enabled: !!ticketId,
  });

  const addMessageMutation = useMutation({
    mutationFn: (content: string) => api.createSupportTicketMessage(ticketId!, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customerSupport.ticketMessages(ticketId!),
      });
    },
  });

  const loading = ticketQuery.isLoading || messagesQuery.isLoading;
  const error = ticketQuery.error?.message ?? messagesQuery.error?.message ?? null;

  return {
    ticket: (ticketQuery.data as TicketDetail) ?? null,
    messages: messagesQuery.data?.messages ?? [],
    loading,
    error,
    refresh: () => {
      ticketQuery.refetch();
      messagesQuery.refetch();
    },
    addMessage: async (content: string) => {
      await addMessageMutation.mutateAsync(content);
    },
  };
}
